"use client";

import React, { useState, useRef } from "react";
import { Upload, X, FileVideo, FileImage, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { api } from "../../lib/api";

interface MediaUploaderProps {
  onUploadComplete: (urls: string[]) => void;
  maxFiles?: number;
  maxSizeMB?: number;
  label?: string;
  initialUrls?: string[];
}

interface FileProgress {
  name: string;
  size: number;
  type: string;
  previewUrl: string;
  fileObj: File;
}

export function MediaUploader({
  onUploadComplete,
  maxFiles = 10,
  maxSizeMB = 50,
  label = "Upload Images or Videos",
  initialUrls = [],
}: MediaUploaderProps) {
  const [selectedFiles, setSelectedFiles] = useState<FileProgress[]>([]);
  const [uploadedUrls, setUploadedUrls] = useState<string[]>(initialUrls);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const processFiles = (files: FileList) => {
    setError(null);
    const newFiles: FileProgress[] = [];
    const sizeLimit = maxSizeMB * 1024 * 1024;

    if (selectedFiles.length + uploadedUrls.length + files.length > maxFiles) {
      setError(`You can only upload a maximum of ${maxFiles} files in total.`);
      return;
    }

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!file.type.startsWith("image/") && !file.type.startsWith("video/")) {
        setError("Only image and video files are supported.");
        continue;
      }
      if (file.size > sizeLimit) {
        setError(`File ${file.name} exceeds the maximum size limit of ${maxSizeMB}MB.`);
        continue;
      }

      newFiles.push({
        name: file.name,
        size: file.size,
        type: file.type,
        previewUrl: URL.createObjectURL(file),
        fileObj: file,
      });
    }

    if (newFiles.length > 0) {
      setSelectedFiles((prev) => [...prev, ...newFiles]);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processFiles(e.dataTransfer.files);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      processFiles(e.target.files);
    }
  };

  const removeSelectedFile = (index: number) => {
    setSelectedFiles((prev) => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[index].previewUrl);
      updated.splice(index, 1);
      return updated;
    });
  };

  const removeUploadedFile = (index: number) => {
    const updated = [...uploadedUrls];
    updated.splice(index, 1);
    setUploadedUrls(updated);
    onUploadComplete(updated);
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const uploadAll = async () => {
    if (selectedFiles.length === 0) return;
    setIsUploading(true);
    setError(null);

    try {
      const filesToUpload = selectedFiles.map((f) => f.fileObj);
      const res = await api.uploadFiles(filesToUpload);
      
      const newUrls = [...uploadedUrls, ...res.urls];
      setUploadedUrls(newUrls);
      onUploadComplete(newUrls);

      // Clean up local preview object URLs
      selectedFiles.forEach((f) => URL.revokeObjectURL(f.previewUrl));
      setSelectedFiles([]);
    } catch (err: any) {
      console.error(err);
      setError("An error occurred during upload. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="w-full bg-slate-900/60 backdrop-blur-md rounded-2xl border border-slate-800 p-6 shadow-2xl transition-all duration-300">
      <h3 className="text-md font-semibold text-slate-200 mb-3 flex items-center gap-2">
        <Upload className="w-4 h-4 text-emerald-400" />
        {label}
      </h3>

      {/* Drag & Drop Zone */}
      <div
        onDragEnter={handleDrag}
        onDragOver={handleDrag}
        onDragLeave={handleDrag}
        onDrop={handleDrop}
        onClick={triggerFileInput}
        className={`relative w-full border-2 border-dashed rounded-xl py-8 px-4 flex flex-col items-center justify-center cursor-pointer transition-all duration-300 ${
          dragActive
            ? "border-emerald-500 bg-emerald-500/10 shadow-[0_0_15px_rgba(16,185,129,0.2)]"
            : "border-slate-700 hover:border-emerald-500/50 hover:bg-slate-800/30"
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,video/*"
          className="hidden"
          onChange={handleChange}
        />

        <div className="p-3 bg-slate-800/80 rounded-full border border-slate-700/80 text-emerald-400 mb-3 shadow-lg group-hover:scale-110 transition-transform">
          <Upload className="w-6 h-6 animate-pulse" />
        </div>

        <p className="text-slate-300 text-sm font-medium text-center">
          Drag & drop your files here, or <span className="text-emerald-400 font-semibold hover:underline">browse</span>
        </p>
        <p className="text-slate-500 text-xs mt-2 text-center">
          Supports high-res Images & Videos (Max {maxSizeMB}MB each, up to {maxFiles} files)
        </p>
      </div>

      {error && (
        <div className="mt-4 p-3 bg-rose-500/10 border border-rose-500/20 text-rose-400 rounded-xl text-xs flex items-start gap-2 shadow-inner">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Previews of Selected (Local) Files */}
      {selectedFiles.length > 0 && (
        <div className="mt-6">
          <div className="flex justify-between items-center mb-3">
            <span className="text-slate-400 text-xs font-semibold uppercase tracking-wider">
              Selected Files ({selectedFiles.length})
            </span>
            <button
              onClick={uploadAll}
              disabled={isUploading}
              className="py-1.5 px-4 bg-emerald-500 hover:bg-emerald-600 active:scale-95 disabled:opacity-50 text-slate-950 font-bold text-xs rounded-lg flex items-center gap-1.5 shadow-md transition-all"
            >
              {isUploading ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <CheckCircle className="w-3.5 h-3.5" />
                  Confirm & Upload
                </>
              )}
            </button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-60 overflow-y-auto pr-1">
            {selectedFiles.map((file, idx) => (
              <div
                key={idx}
                className="relative group bg-slate-800/80 rounded-xl border border-slate-700/60 p-2 overflow-hidden aspect-square flex flex-col justify-between"
              >
                {file.type.startsWith("video/") ? (
                  <div className="w-full h-[70%] bg-slate-950/80 rounded-lg flex items-center justify-center text-slate-400 relative">
                    <FileVideo className="w-8 h-8 text-emerald-400/80" />
                    <span className="absolute bottom-1 right-1 bg-slate-900/90 text-[9px] px-1 rounded text-slate-400 font-mono">
                      VIDEO
                    </span>
                  </div>
                ) : (
                  <div className="w-full h-[70%] rounded-lg overflow-hidden bg-slate-950/40 relative">
                    <img
                      src={file.previewUrl}
                      alt={file.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div className="mt-1.5 truncate text-[10px] text-slate-300 font-medium">
                  {file.name}
                </div>
                <div className="text-[9px] text-slate-500">
                  {(file.size / (1024 * 1024)).toFixed(2)} MB
                </div>

                <button
                  type="button"
                  onClick={() => removeSelectedFile(idx)}
                  className="absolute -top-1 -right-1 p-1 bg-slate-950/90 hover:bg-rose-500 rounded-full border border-slate-700 text-slate-400 hover:text-white transition-colors shadow-md group-hover:scale-105"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Previews of Uploaded (Server) Files */}
      {uploadedUrls.length > 0 && (
        <div className="mt-6 border-t border-slate-800/80 pt-4">
          <span className="text-slate-400 text-xs font-semibold uppercase tracking-wider block mb-3">
            Uploaded Media ({uploadedUrls.length})
          </span>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-60 overflow-y-auto pr-1">
            {uploadedUrls.map((url, idx) => {
              const isVideo = url.endsWith(".mp4") || url.includes("video") || url.includes("ForBiggerBlazes");
              return (
                <div
                  key={idx}
                  className="relative group bg-emerald-950/20 rounded-xl border border-emerald-500/20 p-2 overflow-hidden aspect-square flex flex-col justify-between shadow-lg"
                >
                  {isVideo ? (
                    <div className="w-full h-[80%] bg-slate-950/80 rounded-lg flex items-center justify-center text-slate-400 relative">
                      <FileVideo className="w-8 h-8 text-emerald-400" />
                      <span className="absolute bottom-1 right-1 bg-slate-900/90 text-[9px] px-1 rounded text-emerald-400 font-mono">
                        UPLOADED
                      </span>
                    </div>
                  ) : (
                    <div className="w-full h-[80%] rounded-lg overflow-hidden bg-slate-950/40 relative">
                      <img
                        src={url}
                        alt="Uploaded company file"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="text-[9px] text-emerald-400/95 font-semibold text-center py-0.5 truncate flex items-center justify-center gap-1">
                    <CheckCircle className="w-2.5 h-2.5" /> Active Link
                  </div>

                  <button
                    type="button"
                    onClick={() => removeUploadedFile(idx)}
                    className="absolute -top-1 -right-1 p-1 bg-slate-950/90 hover:bg-rose-500 rounded-full border border-slate-700 text-slate-400 hover:text-white transition-colors shadow-md group-hover:scale-105"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
